const BOOKING_SALE_FULL_EDIT_ROLES = [
  "super_admin",
  "admin",
  "accountant",
  "sleep_technician",
];

export const BOOKING_SALE_FILTER_ALL = "All Sale";
export const BOOKING_SALE_FILTER_UNASSIGNED = "Unassigned Sale";

const normalizeText = (value) =>
  String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getRoles = (userOrRoles) => {
  if (Array.isArray(userOrRoles)) return userOrRoles;
  return userOrRoles && Array.isArray(userOrRoles.roles)
    ? userOrRoles.roles
    : [];
};

export const normalizeBookingSaleName = (value) => {
  const normalizedValue = normalizeText(value);
  const nameWithoutPrefix = normalizedValue.includes(".")
    ? normalizedValue.slice(normalizedValue.indexOf(".") + 1)
    : normalizedValue;

  return normalizeText(nameWithoutPrefix).toLocaleLowerCase("en-US");
};

export const bookingSaleNamesMatch = (left, right) => {
  const leftKey = normalizeBookingSaleName(left);
  const rightKey = normalizeBookingSaleName(right);
  return Boolean(leftKey && rightKey && leftKey === rightKey);
};

export const findCanonicalBookingSaleName = (displayName, saleNames = []) =>
  saleNames.find((saleName) =>
    bookingSaleNamesMatch(saleName, displayName),
  ) || "";

export const canSelectAnyBookingSale = (userOrRoles) =>
  getRoles(userOrRoles).some((role) =>
    BOOKING_SALE_FULL_EDIT_ROLES.includes(role),
  );

export const isBookingSaleScopedUser = (userOrRoles) => {
  const roles = getRoles(userOrRoles);
  return (
    roles.includes("sale") &&
    !roles.some((role) => BOOKING_SALE_FULL_EDIT_ROLES.includes(role))
  );
};

export const canEditBookingSale = (userOrRoles) =>
  canSelectAnyBookingSale(userOrRoles) ||
  isBookingSaleScopedUser(userOrRoles);

const uniqueSaleNames = (saleNames = []) =>
  saleNames.reduce((names, saleName) => {
    const normalizedName = normalizeText(saleName);
    if (
      normalizedName &&
      !names.some((name) => name === normalizedName)
    ) {
      names.push(normalizedName);
    }
    return names;
  }, []);

export const getBookingSaleOptions = ({
  currentUser,
  saleNames = [],
  currentSale = "",
}) => {
  const normalizedCurrentSale = normalizeText(currentSale);
  const selectableSaleNames = uniqueSaleNames(saleNames);

  if (canSelectAnyBookingSale(currentUser)) {
    const options = ["กรุณาเลือก", ...selectableSaleNames];
    if (
      normalizedCurrentSale &&
      !options.includes(normalizedCurrentSale)
    ) {
      options.push(normalizedCurrentSale);
    }
    return options;
  }

  if (isBookingSaleScopedUser(currentUser)) {
    const ownCanonicalName = findCanonicalBookingSaleName(
      currentUser && currentUser.displayName,
      selectableSaleNames,
    );
    const ownName =
      ownCanonicalName || normalizeText(currentUser && currentUser.displayName);
    return ownName ? [ownName] : [];
  }

  return normalizedCurrentSale
    ? [normalizedCurrentSale]
    : ["กรุณาเลือก"];
};

export const getBookingSaleSaveValue = ({
  currentUser,
  saleNames = [],
  selectedSale = "",
  currentSale = "",
}) => {
  if (canSelectAnyBookingSale(currentUser)) {
    return selectedSale === "กรุณาเลือก"
      ? ""
      : normalizeText(selectedSale);
  }

  if (isBookingSaleScopedUser(currentUser)) {
    return (
      findCanonicalBookingSaleName(
        currentUser && currentUser.displayName,
        saleNames,
      ) || normalizeText(currentUser && currentUser.displayName)
    );
  }

  return normalizeText(currentSale);
};

export const filterBookingsBySaleSelection = ({
  records = [],
  selectedSale = BOOKING_SALE_FILTER_ALL,
  getSaleValue = (record) =>
    record && record.extendedProps && record.extendedProps.sale,
}) => {
  if (!selectedSale || selectedSale === BOOKING_SALE_FILTER_ALL) {
    return records;
  }

  if (selectedSale === BOOKING_SALE_FILTER_UNASSIGNED) {
    return records.filter((record) => {
      const saleValue = normalizeText(getSaleValue(record));
      return !saleValue || saleValue === "กรุณาเลือก";
    });
  }

  return records.filter((record) =>
    bookingSaleNamesMatch(getSaleValue(record), selectedSale),
  );
};

export const isLphBookingNode = (nodeName, nodes = []) => {
  const normalizedNodeName = normalizeText(nodeName).toLocaleLowerCase("en-US");
  const matchingNode = nodes.find(
    (node) =>
      normalizeText(node && node.nodename).toLocaleLowerCase("en-US") ===
      normalizedNodeName,
  );
  const normalizedNodeCode = normalizeText(
    matchingNode && matchingNode.nodecode,
  ).toLocaleUpperCase("en-US");

  return (
    normalizedNodeCode === "LPH" ||
    normalizedNodeName === "lph" ||
    /(^|\W)lph($|\W)/i.test(normalizedNodeName) ||
    normalizedNodeName.includes("ladprao")
  );
};

export const filterBookingsForSaleAccess = ({
  records = [],
  currentUser,
  assignedNodes = [],
  getNodeValue = (record) =>
    record && record.extendedProps && record.extendedProps.node,
  getSaleValue = (record) =>
    record && record.extendedProps && record.extendedProps.sale,
}) => {
  if (!isBookingSaleScopedUser(currentUser)) return records;

  const allowedNodeNames = new Set(
    assignedNodes
      .map((node) =>
        normalizeText(node && node.nodename).toLocaleLowerCase("en-US"),
      )
      .filter(Boolean),
  );
  const currentSaleName = currentUser && currentUser.displayName;

  return records.filter((record) => {
    const nodeName = normalizeText(getNodeValue(record));
    const nodeKey = nodeName.toLocaleLowerCase("en-US");
    if (!nodeName || !allowedNodeNames.has(nodeKey)) return false;

    if (!isLphBookingNode(nodeName, assignedNodes)) return true;
    return bookingSaleNamesMatch(getSaleValue(record), currentSaleName);
  });
};
